import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import api from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { useExpenditureLabels, useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';
import ContractEditor from '../../../components/ContractEditor';
import {
  Plus, Edit, Trash2, Save, Search, Download, X,
  FileText, Users, ChevronRight, ChevronDown, Bell,
  Lock, Eye, Pencil, Settings, Package
} from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../../../components/SortableTableHeader';

export function ContractTemplatesPanel() {
  const [templates, setTemplates] = useState([]);
  const [tplSort, handleTplSort] = useSortState('name', 'asc');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null=list, 'new'=add, object=edit
  const [form, setForm] = useState({ name: '', description: '', category: '', content: '', active: true });

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/contracts/templates', { params: { search } });
      setTemplates(Array.isArray(res.data) ? res.data : []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSave = async () => {
    try {
      if (editing === 'new') {
        await api.post('/api/contracts/templates', form);
        toast.success('Template created');
      } else {
        await api.put(`/api/contracts/templates/${editing.id}`, form);
        toast.success('Template updated');
      }
      setEditing(null);
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/contracts/templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const openEdit = (t) => {
    setForm({ name: t.name || '', description: t.description || '', category: t.category || '', content: t.content || '', active: t.active !== false });
    setEditing(t);
  };

  const openNew = () => {
    setForm({ name: '', description: '', category: '', content: '', active: true });
    setEditing('new');
  };

  // Template edit form
  if (editing) {
    return (
      <div data-testid="contract-template-edit">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">Contracts</h1>
            <p className="text-sm text-muted-foreground">{editing === 'new' ? 'Add New Template' : `Editing: ${form.name}`}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setEditing(null)}><X className="h-4 w-4 mr-1" /> Back to List</Button>
        </div>
        <div className="border rounded-lg p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label className="text-xs">Template Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} data-testid="ct-name" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Category</Label>
              <select value={form.category} onChange={e => setForm(p => ({...p, category: e.target.value}))}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="ct-category">
                <option value="">-- Select --</option>
                {['Service Agreement','NDA','Master Agreement','Licensing','Employment','Purchase','Vendor','General'].map(c =>
                  <option key={c} value={c}>{c}</option>
                )}
              </select></div>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Description</Label>
            <Input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} data-testid="ct-desc" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Template Content</Label>
            <ContractEditor content={form.content} onChange={val => setForm(p => ({...p, content: val}))} readOnly={false} /></div>
          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={v => setForm(p => ({...p, active: v}))} data-testid="ct-active" />
            <Label className="text-sm">Active</Label>
          </div>
          <p className="text-xs text-muted-foreground">
            Available placeholders: {'{{company_name}}'}, {'{{supplier_name}}'}, {'{{contract_number}}'}, {'{{effective_date}}'}, {'{{expiry_date}}'}, {'{{contract_value}}'}, {'{{currency}}'}, {'{{signatory_name}}'}, {'{{signatory_title}}'}
          </p>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button onClick={handleSave} data-testid="ct-save-btn"><Save className="h-4 w-4 mr-1" /> Save</Button>
          <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
        </div>
      </div>
    );
  }

  // Template list view
  return (
    <div data-testid="contract-templates-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">Contracts</h1>
          <p className="text-sm text-muted-foreground">Contract Templates — manage templates used in Supplier Contracts</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{templates.length} templates</span>
          <Button onClick={openNew} data-testid="ct-add-btn"><Plus className="h-4 w-4 mr-1" /> Add Template</Button>
        </div>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search templates..." className="w-64" />
        <Button size="sm" onClick={fetchTemplates}><Search className="h-3 w-3 mr-1" /> Search</Button>
        <Button size="sm" variant="outline" onClick={() => { setSearch(''); setTimeout(fetchTemplates, 100); }}><X className="h-3 w-3 mr-1" /> Clear</Button>
      </div>
      {loading ? <LoadingSpinner /> : templates.length === 0 ? <EmptyState title="No templates found" description="Click 'Add Template' to create your first contract template." /> : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <SortableTableHeader label="Name" field="name" sortState={tplSort} onSort={handleTplSort} />
                <SortableTableHeader label="Category" field="category" sortState={tplSort} onSort={handleTplSort} />
                <SortableTableHeader label="Description" field="description" sortState={tplSort} onSort={handleTplSort} />
                <SortableTableHeader label="System" field="is_system" sortState={tplSort} onSort={handleTplSort} className="text-center" />
                <SortableTableHeader label="Active" field="active" sortState={tplSort} onSort={handleTplSort} className="text-center" />
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {sortData(templates, tplSort.field, tplSort.dir).map(t => (
                <tr key={t.id} className="border-t hover:bg-accent/50 cursor-pointer" onClick={() => openEdit(t)}>
                  <td className="px-3 py-2 font-medium text-primary">{t.name}</td>
                  <td className="px-3 py-2"><Badge variant="secondary" className="text-[10px]">{t.category || '—'}</Badge></td>
                  <td className="px-3 py-2 text-muted-foreground text-xs truncate max-w-xs">{t.description || '—'}</td>
                  <td className="px-3 py-2 text-center">{t.is_system_template ? <Badge variant="outline" className="text-[10px] border-zinc-300 text-zinc-600 bg-zinc-50">System</Badge> : <span className="text-zinc-400">—</span>}</td>
                  <td className="px-3 py-2 text-center">{t.active !== false ? 'Yes' : 'No'}</td>
                  <td className="px-2" onClick={e => e.stopPropagation()}>
                    {!t.is_system_template && <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(t.id)} data-testid={`ct-del-${t.id}`}><Trash2 className="h-3 w-3" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
